import type {
  ArtistSummary,
  ArtistSummaryRow,
  Breakdown,
  ChartmetricSnapshot,
  Composition,
  EventsResponse,
  Me,
  PeerSet,
  PeersResponse,
  Selectors,
} from '@dashboard/contract';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, getJson, query, sendJson } from './client';

export const useMe = () =>
  useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => getJson<Me>('/api/me', signal),
    staleTime: 5 * 60_000,
  });

export const useArtists = () =>
  useQuery({
    queryKey: ['artists'],
    queryFn: ({ signal }) => getJson<ArtistSummaryRow[]>('/api/artists', signal),
    staleTime: 5 * 60_000,
  });

export const useSummary = (artistId: string) =>
  useQuery({
    queryKey: ['summary', artistId],
    queryFn: ({ signal }) => getJson<ArtistSummary>(`/api/artists/${artistId}/summary`, signal),
  });

export const useBreakdown = (artistId: string, sel: Selectors) =>
  useQuery({
    queryKey: ['breakdown', artistId, sel],
    queryFn: ({ signal }) =>
      getJson<Breakdown>(`/api/artists/${artistId}/breakdown${query(sel)}`, signal),
    placeholderData: keepPreviousData,
  });

export const useComposition = (artistId: string, sel: Selectors) =>
  useQuery({
    queryKey: ['composition', artistId, sel],
    queryFn: ({ signal }) =>
      getJson<Composition>(`/api/artists/${artistId}/composition${query(sel)}`, signal),
    placeholderData: keepPreviousData,
  });

export const useEvents = (artistId: string, cls: 'all' | 'revival' | 'campaign') =>
  useQuery({
    queryKey: ['events', artistId, cls],
    queryFn: ({ signal }) =>
      getJson<EventsResponse>(`/api/artists/${artistId}/events${query({ class: cls })}`, signal),
    placeholderData: keepPreviousData,
  });

export const usePeers = (artistId: string, cat: number, peers: string[], peerSetId?: string) =>
  useQuery({
    queryKey: ['peers', artistId, cat, peers, peerSetId],
    queryFn: ({ signal }) =>
      getJson<PeersResponse>(
        `/api/artists/${artistId}/peers${query({ cat, peers: peers.join(','), peerSetId })}`,
        signal,
      ),
    placeholderData: keepPreviousData,
  });

export const useChartmetric = (artistId: string) =>
  useQuery({
    queryKey: ['chartmetric', artistId],
    queryFn: async ({ signal }) => {
      try {
        return await getJson<ChartmetricSnapshot>(`/api/artists/${artistId}/chartmetric`, signal);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    staleTime: 60 * 60_000,
  });

export const usePeerSets = () =>
  useQuery({
    queryKey: ['peer-sets'],
    queryFn: ({ signal }) => getJson<PeerSet[]>('/api/peer-sets', signal),
  });

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => sendJson<{ redirectTo: string }>('POST', '/auth/logout'),
    onSuccess: ({ redirectTo }) => {
      qc.clear();
      window.location.assign(redirectTo);
    },
  });
}
